import { useToast, ContextMenuItem } from '@cad-arc';
import React, { useCallback } from 'react';
import { useApiClient } from '~/api-client';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { ScheduledEventQueueRowData } from '../socket/stream-types';
import { useOpenScheduledEventPanel } from '../event-panel/use-open-event-panel';
import { v4 } from 'uuid';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { EventResource } from '@mark43/cad-api';

export const ScheduledEventContextMenu = ({
    data,
}: {
    data: ScheduledEventQueueRowData;
}): React.ReactElement => {
    const apiClient = useApiClient();
    const scheduledEventView = data;
    const toast = useToast();
    const enabledFeatures = useEnabledFeatures();
    const userCanClearEvent = enabledFeatures.editUnassignedEvents && enabledFeatures.clearEvent;
    const openScheduledEventPanel = useOpenScheduledEventPanel();
    const { trigger } = useIntegratedActionBroadcaster();

    const activateScheduledEvent = useCallback(
        (id: number) => {
            return apiClient.request<EventResource.ExecuteScheduledAgencyEvent>({
                method: 'POST',
                url: `event/execute_scheduled_event/${id}/`,
            });
        },
        [apiClient]
    );

    const clearScheduledEvent = useCallback(
        (id: number) => {
            openScheduledEventPanel({
                eventId: id,
            });
            trigger([
                {
                    targetWindowType: 'EVENT_V2',
                    id: v4(),
                    type: 'SHOW_CLEAR_EVENT_MODAL',
                    payload: { eventId: id },
                },
            ]);
        },
        [openScheduledEventPanel, trigger]
    );

    return (
        <ErrorBoundary
            name="SCHEDULED_EVENT_CONTEXT_MENU"
            messageVerbNoun="opening the scheduled event menu"
            asToast={true}
        >
            <ContextMenuItem
                onSelect={() => {
                    activateScheduledEvent(scheduledEventView.id).catch(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('activating scheduled event'),
                        });
                    });
                }}
                isDisabled={scheduledEventView.scheduledRecurring !== 'ONCE'}
            >
                Activate Event
            </ContextMenuItem>
            {userCanClearEvent && (
                <ContextMenuItem
                    onSelect={() => {
                        clearScheduledEvent(scheduledEventView.id);
                    }}
                >
                    Clear Event
                </ContextMenuItem>
            )}
        </ErrorBoundary>
    );
};
